import { useChatScroll } from '@/components/chat/useChatScroll';
import { currentLocale } from '@/lib/locale';
import { todayAR } from '@/../../shared/dates';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DTALK_MAX_MESSAGE_CHARS } from '@/../../shared/types/dtalk';
import { Send, Sparkles, Check, Lock, AlertCircle, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAutoResizeTextarea } from '@/components/ui/animated-ai-chat';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';
import { VoiceInput } from '@/components/performance/VoiceInput';
import { AiSpeakingOrb } from '@/components/performance/AiSpeakingOrb';
import { useMyDTalk, useProcessDTalkMessage, useReopenDTalk } from '@/hooks/usePerformance';
import { computeDTalkProgress } from '@/../../shared/dtalk-progress';
import { useSpokenReply } from '@/hooks/useSpokenReply';
import { AGENT_VOICE, STT_LANG } from '@/lib/speech-utils';
import { DTalkHeader } from '@/pages/performance/dtalk/DTalkLayout';
import { effectiveDTalkStatus } from '@/../../shared/dtalk-stats';
import { dtalkChip, getInitials, getAvatarColor, type DTalkProcessStatus } from '@/components/performance/dtalk/dtalkShared';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

type DTalkIdiomaOpt = 'es' | 'pt' | 'en';

// Espejo de DTALK_LANGUAGE_GREETING (backend/src/lib/dtalk-agent.ts) — texto EXACTO
const LANGUAGE_GREETING = "Hello! 👋 Let's get started. What language would you like to use?";

// El selector muestra cada idioma escrito en sí mismo, a propósito: no se traduce.
const LANGUAGE_OPTIONS: { idioma: DTalkIdiomaOpt; label: string }[] = [
  { idioma: 'es', label: 'Español' }, // i18n-ok: nombre del idioma en su propio idioma
  { idioma: 'pt', label: 'Português' }, // i18n-ok: nombre del idioma en su propio idioma
  { idioma: 'en', label: 'English' }, // i18n-ok: nombre del idioma en su propio idioma
];

// La conversación sigue el idioma elegido — también los mensajes enlatados.
// (La reapertura ya no es un mensaje: es una mutation determinística.)
const CANNED_MESSAGES: Record<DTalkIdiomaOpt, { validate: string; adjust: string }> = {
  es: {
    validate: 'Sí, la conclusión refleja correctamente lo que quería transmitir.',
    adjust: 'Quiero ajustar algo de la conclusión.',
  },
  pt: {
    validate: 'Sim, a conclusão reflete corretamente o que eu queria transmitir.',
    adjust: 'Quero ajustar algo da conclusão.',
  },
  en: {
    validate: 'Yes, the conclusion correctly reflects what I wanted to convey.',
    adjust: "I'd like to adjust something in the conclusion.",
  },
};

interface Msg { role: 'user' | 'assistant'; content: string; timestamp: string; }

export function PerformanceDTalk() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useMyDTalk();
  const processMessage = useProcessDTalkMessage();
  const reopen = useReopenDTalk();

  const [input, setInput] = useState('');
  const [tab, setTab] = useState<'conversation' | 'summary'>('conversation');
  const [localMessages, setLocalMessages] = useState<Msg[] | null>(null);
  // Error del último envío: banner con "Reintentar" (el texto vuelve al input)
  const [sendError, setSendError] = useState<{ msg: string; idioma?: DTalkIdiomaOpt; motivo?: 'too_long' } | null>(null);
  // Progreso reportado por la mutation (avanza al toque, sin esperar el refetch)
  const [turnProgress, setTurnProgress] = useState(0);

  // ── Voz ────────────────────────────────────────────────────────────────────
  const [recording, setRecording] = useState(false);
  // Cubre la ventana entre elegir idioma y que la invalidación traiga record.idioma
  const [chosenIdioma, setChosenIdioma] = useState<DTalkIdiomaOpt | null>(null);
  const [speakerOn, setSpeakerOn] = useState(() => localStorage.getItem('dtalk.speaker') === '1');
  const lastInputWasVoiceRef = useRef(false);
  const { speak, stop: stopSpeaking, isSpeaking } = useSpokenReply();

  // ── Textarea que crece ────────────────────────────────────────────────────
  // Era un renglón fijo: el dictado entraba por debajo, se veía solo la primera
  // línea y había que ir "a narnia" a buscar el final para apretar Enter. El
  // hook es el del Brain, pero ahí se llama en onChange y eso no cubre un
  // setInput programático (mic, "Reintentar", el '' post-envío): acá va en un
  // effect sobre `input`. Y se espera a que `recording` vuelva a false porque
  // mientras graba el textarea está hidden y scrollHeight mide 0.
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({ minHeight: 36, maxHeight: 160 });
  // Al terminar de dictar el ✓ se desmonta y se lleva el foco: lo devolvemos
  // al final del texto, que es donde la persona quiere estar para dar Enter.
  const focusAfterVoiceRef = useRef(false);
  useEffect(() => {
    if (recording) return;
    adjustHeight();
    if (!focusAfterVoiceRef.current) return;
    focusAfterVoiceRef.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollTop = el.scrollHeight;
  }, [input, recording, adjustHeight, textareaRef]);

  const toggleSpeaker = () => {
    setSpeakerOn(prev => {
      const next = !prev;
      localStorage.setItem('dtalk.speaker', next ? '1' : '0');
      if (!next) stopSpeaking();
      return next;
    });
  };

  const record = data?.record ?? null;
  const serverMsgs: Msg[] = (record?.conversationHistory as Msg[]) ?? [];
  const messages = localMessages ?? serverMsgs;
  const endRef = useChatScroll([messages, processMessage.isPending]);
  // Re-adoptar el historial del server cuando trae MÁS mensajes que lo local:
  // cubre el primer render, el resync tras un error de red y multi-tab. (El
  // guard viejo `localMessages === null` congelaba el chat tras un fallo —
  // había que recargar la página para ver la respuesta ya persistida.)
  useEffect(() => {
    if (!processMessage.isPending && serverMsgs.length > (localMessages?.length ?? 0)) {
      setLocalMessages(serverMsgs);
      // Si veníamos de un error de envío y el server igual persistió el turno
      // (fetch cortado con Lambda completada), reintentar lo duplicaría.
      if (sendError) { setSendError(null); setInput(''); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMsgs.length, processMessage.isPending]);

  const eligibility = data?.eligibility;
  /** Cuándo cumple los 3 meses (para decirle desde cuándo puede participar). */
  const eligibleFrom = (() => {
    const raw = data?.employee?.startDate;
    if (!raw) return null;
    const iso = /^\d{4}-\d{2}-\d{2}/.test(raw.trim())
      ? raw.trim().slice(0, 10)
      : raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)?.slice(1).reverse().map((v, i) => (i === 0 ? v : v.padStart(2, '0'))).join('-');
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCMonth(dt.getUTCMonth() + 3);
    return dt.toISOString().slice(0, 10);
  })();
  const windowOpen = data?.windowOpen ?? true;
  // Distinguir "todavía no abrió" (antes de windowStart) de "cerrada" (después de windowEnd).
  // La ventana está en fechas de Buenos Aires: compararla con hoy UTC la abría 3 h antes.
  const beforeWindow = !!data?.cycle && todayAR() < data.cycle.windowStart;
  // Efectivo: si el botón de licencia lo dejó en "Sin iniciar" con la charla hecha, manda la evidencia.
  const status = (record ? effectiveDTalkStatus(record) : 'not_started') as DTalkProcessStatus;
  const emp = data?.employee;
  const fullName = emp ? `${emp.name} ${emp.lastName}`.trim() : '';

  // Progreso: el persistido del record (o el fallback local con la MISMA
  // fórmula de shared/dtalk-progress) contra lo que devolvió el último turno.
  // Monótono: nunca retrocede en pantalla.
  const seededProgress = useMemo(() => {
    return record?.progressPct ?? computeDTalkProgress({
      userTurns: serverMsgs.filter(m => m.role === 'user').length,
      cantidadPreguntasPrincipales: record?.cantidadPreguntasPrincipales,
      estadoActual: record?.estadoActual,
      aspiracionPrincipal: record?.aspiracionPrincipal,
      competenciasADesarrollar: record?.competenciasADesarrollar,
      responsabilidadesInteres: record?.responsabilidadesInteres,
      processStatus: record?.processStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);
  const displayProgress = Math.max(seededProgress, turnProgress);

  // "Alertas" NO se muestra al clapper: es información del lead sobre sus reportes
  // (feedback Sol 06/08). Vive solo en las vistas de líder/People.
  const understanding = [
    { key: 'u_estado', done: !!record?.estadoActual },
    { key: 'u_aspiracion', done: !!record?.aspiracionPrincipal },
    { key: 'u_competencias', done: !!(record?.competenciasADesarrollar?.length) },
    { key: 'u_responsabilidades', done: !!(record?.responsabilidadesInteres?.length) },
  ];

  // Idioma efectivo de la conversación (para STT y voz de la respuesta)
  const effIdioma: DTalkIdiomaOpt = (record?.idioma as DTalkIdiomaOpt) ?? chosenIdioma ?? 'es';

  async function send(text: string, idioma?: DTalkIdiomaOpt) {
    const msg = text.trim();
    if (!msg || processMessage.isPending) return;
    // El backend rechaza más de DTALK_MAX_MESSAGE_CHARS con un 400 que acá
    // llegaba como "D-Talk: error 400" y un "Reintentar" condenado a fallar
    // siempre (incidente 01/10/2026). Se corta antes, con el motivo, y lo
    // escrito queda en el cuadro para poder acortarlo.
    if (msg.length > DTALK_MAX_MESSAGE_CHARS) { setSendError({ msg, idioma, motivo: 'too_long' }); return; }
    const wasVoice = lastInputWasVoiceRef.current;
    lastInputWasVoiceRef.current = false;
    setInput('');
    setSendError(null);
    setLocalMessages(prev => [...(prev ?? serverMsgs), { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    try {
      const res = await processMessage.mutateAsync({ message: msg, ...(idioma ? { idioma } : {}) });
      setLocalMessages(prev => [...(prev ?? []), { role: 'assistant', content: res.reply, timestamp: new Date().toISOString() }]);
      if (typeof res.progressPct === 'number') setTurnProgress(p => Math.max(p, res.progressPct!));
      // Si el mensaje se dictó por voz (o el altavoz está prendido), la agente contesta hablando
      const lang = idioma ?? effIdioma;
      if ((wasVoice || speakerOn) && !res.notEligible && res.reply) {
        void speak(res.reply, { voice: AGENT_VOICE[lang], browserLang: STT_LANG[lang] });
      }
    } catch {
      // El texto vuelve al input y aparece el banner con "Reintentar". Además
      // resincronizamos con el server: si la Lambda terminó pero el fetch
      // murió (red/timeout), la respuesta ya está persistida y aparece sola —
      // antes había que salir y volver a entrar.
      setSendError({ msg, idioma });
      setInput(msg);
      setLocalMessages(null);
      refetch();
    }
  }

  /** Selector inicial: fija el idioma de TODA la conversación (pedido Sol 12/08). */
  function chooseLanguage(opt: { idioma: DTalkIdiomaOpt; label: string }) {
    setChosenIdioma(opt.idioma);
    // El greeting queda como primera burbuja local; el backend lo persiste igual
    setLocalMessages([{ role: 'assistant', content: LANGUAGE_GREETING, timestamp: new Date().toISOString() }]);
    send(opt.label, opt.idioma);
  }

  // Textos enlatados de validar/ajustar/reabrir en el idioma elegido
  const canned = CANNED_MESSAGES[(record?.idioma as DTalkIdiomaOpt) ?? 'es'] ?? CANNED_MESSAGES.es;

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  }

  // No elegible (§7.1 / §9.1)
  if (eligibility && eligibility.status !== 'eligible') {
    return (
      <div className="space-y-5">
        <DTalkHeader title={t('performance.dtalk.conv.title')} cycleLabel={data?.cycle?.cycleLabel} />
        <Card>
          <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
            <AlertCircle className="h-8 w-8 text-amber-500" />
            <p className="font-semibold">
              {eligibility.status === 'onboarding'
                ? t('performance.dtalk.conv.onboarding_title')
                : t('performance.dtalk.conv.not_eligible_title')}
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              {eligibility.status === 'onboarding'
                ? (eligibleFrom
                    ? t('performance.dtalk.conv.onboarding_body_with_date', {
                        date: new Date(`${eligibleFrom}T12:00:00Z`).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long', year: 'numeric' }),
                      })
                    : t('performance.dtalk.conv.onboarding_body'))
                : eligibility.status === 'license'
                  ? t('performance.dtalk.conv.license_body')
                  : eligibility.reason}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showValidateButtons = status === 'pending_validation' && !!record?.conclusionParaValidar;
  const isCompleted = status === 'completed';

  return (
    <div className="space-y-5">
      <DTalkHeader title={t('performance.dtalk.conv.title')} cycleLabel={data?.cycle?.cycleLabel} />

      {/* Tabs Conversación / Resumen */}
      <div className="flex gap-2">
        {(['conversation', 'summary'] as const).map(tk => (
          <button key={tk} onClick={() => setTab(tk)}
            className={cn('rounded-lg px-4 py-2 text-sm font-semibold border transition-colors',
              tab === tk ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-accent')}>
            {t(`performance.dtalk.conv.tab_${tk}`)}
          </button>
        ))}
      </div>

      {/* Person header card */}
      <Card>
        <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold shrink-0 text-gray-700"
                 style={{ backgroundColor: getAvatarColor(fullName || 'D T') }}>
              {getInitials(emp?.name, emp?.lastName)}
            </div>
            <div className="min-w-0">
              <p className="font-bold truncate">{fullName || '—'}</p>
              <p className="text-sm text-teal-600 truncate">{[emp?.role, emp?.area].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
          <div className="text-right text-sm shrink-0">
            <p className="text-muted-foreground">{t('performance.dtalk.conv.state', { status: t(dtalkChip(status, record?.eligibilityStatus).labelKey) })}</p>
            <p className="text-teal-600 inline-flex items-center gap-1 justify-end"><Check className="h-3.5 w-3.5" />{t('performance.dtalk.conv.autosave')}</p>
          </div>
        </CardContent>
      </Card>

      {tab === 'summary' ? (
        <SummaryPanel record={record} />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 items-start">
        {/* Chat */}
        <Card className="flex flex-col h-[calc(100vh-22rem)] min-h-[440px]">
          <CardContent className="flex-1 overflow-y-auto space-y-4 pt-4">
            {messages.length === 0 && (
              <div className="rounded-xl border border-teal-200 bg-teal-50/60 dark:bg-teal-950/10 p-3 flex gap-2 text-sm text-teal-800 dark:text-teal-200">
                <Lock className="h-4 w-4 shrink-0 mt-0.5" /><span>{t('performance.dtalk.conv.confidential')}</span>
              </div>
            )}
            {/* Selector de idioma: primer contacto con el chat (pedido Sol 12/08) */}
            {messages.length === 0 && (
              <>
                <div className="flex items-end gap-2 justify-start">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border shrink-0 mb-1"><Sparkles className="h-4 w-4 text-foreground/70" /></div>
                  <div className="max-w-[85%] rounded-2xl px-4 py-3 text-[15px] bg-card border text-foreground rounded-bl-sm">
                    <p className="leading-relaxed">{LANGUAGE_GREETING}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pl-10">
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <Button key={opt.idioma} size="sm" variant="outline" className="rounded-full"
                            disabled={processMessage.isPending} onClick={() => chooseLanguage(opt)}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={cn('flex items-end gap-2', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                {m.role === 'assistant' && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border shrink-0 mb-5"><Sparkles className="h-4 w-4 text-foreground/70" /></div>
                )}
                <div className={cn('max-w-[85%] rounded-2xl px-4 py-3 text-[15px]',
                  m.role === 'user' ? 'bg-foreground text-background rounded-br-sm' : 'bg-card border text-foreground rounded-bl-sm')}>
                  {m.role === 'user' ? <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p> : <MarkdownText className="leading-relaxed">{m.content}</MarkdownText>}
                  <p className={cn('text-xs mt-1', m.role === 'user' ? 'text-background/60' : 'text-muted-foreground')}>{safeTime(m.timestamp)}</p>
                </div>
              </div>
            ))}
            {processMessage.isPending && (
              <div className="flex items-end gap-2 justify-start">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border shrink-0"><Sparkles className="h-4 w-4 text-foreground/70" /></div>
                <div className="bg-card border rounded-2xl rounded-bl-sm px-4 py-3"><WinclapLoader size={20} /></div>
              </div>
            )}
            <div ref={endRef} />
          </CardContent>

          {/* Validación explícita (§9.8) */}
          {showValidateButtons && (
            <div className="border-t p-3 flex flex-wrap gap-2">
              <Button size="sm" className="gap-1.5 bg-teal-600 hover:bg-teal-700" disabled={processMessage.isPending}
                      onClick={() => send(canned.validate)}>
                <Check className="h-4 w-4" />{t('performance.dtalk.conv.validate_cta')}
              </Button>
              <Button size="sm" variant="outline" disabled={processMessage.isPending}
                      onClick={() => send(canned.adjust)}>
                {t('performance.dtalk.conv.adjust_cta')}
              </Button>
            </div>
          )}
          {/* Conversación cerrada: sin input; solo el estado + reabrir (instantáneo, server-side) */}
          {isCompleted && (
            <div className="border-t p-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                <Check className="h-4 w-4 text-emerald-600" />
                {t('performance.dtalk.conv.completed_banner')}
              </p>
              {windowOpen && (
                <Button size="sm" variant="outline" className="gap-1.5" disabled={reopen.isPending}
                        onClick={() => reopen.mutate(undefined, {
                          onSuccess: () => { setLocalMessages(null); setTurnProgress(0); },
                        })}>
                  {reopen.isPending ? <WinclapLoader size={14} /> : <RotateCcw className="h-4 w-4" />}
                  {t('performance.dtalk.conv.reopen_cta')}
                </Button>
              )}
            </div>
          )}
          {!windowOpen && !isCompleted && (
            <div className="border-t p-3 text-xs text-muted-foreground text-center">
              {beforeWindow
                ? t('performance.dtalk.conv.window_not_open_yet', { date: fmtDate(data?.cycle?.windowStart) })
                : t('performance.dtalk.conv.window_closed')}
            </div>
          )}

          {/* Orbe: la IA está hablando */}
          <AiSpeakingOrb speaking={isSpeaking} floating size={96} />

          {/* Error de envío: el texto ya volvió al input; ofrecemos reintentar */}
          {sendError && (
            <div className="border-t px-3 py-2 flex items-center justify-between gap-3 bg-red-50 dark:bg-red-950/20">
              <p className="text-xs text-red-700 dark:text-red-300 inline-flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {sendError.motivo === 'too_long'
                  ? t('performance.dtalk.conv.send_error_too_long', { chars: sendError.msg.length, max: DTALK_MAX_MESSAGE_CHARS })
                  : t('performance.dtalk.conv.send_error')}
              </p>
              {sendError.motivo !== 'too_long' && (
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={processMessage.isPending}
                        onClick={() => send(sendError.msg, sendError.idioma)}>
                  {t('performance.dtalk.conv.retry')}
                </Button>
              )}
            </div>
          )}

          {/* Input — desaparece cuando la conversación está completada (cerrada) */}
          {windowOpen && !showValidateButtons && !isCompleted && messages.length > 0 && (
            <div className="border-t p-3">
             <div className="flex items-end gap-2">
              <Button
                type="button" variant="ghost" size="icon" onClick={toggleSpeaker}
                className={cn('rounded-full shrink-0', speakerOn && 'text-teal-600 bg-teal-50 dark:bg-teal-950/30', isSpeaking && 'animate-pulse')}
                title={isSpeaking ? t('performance.dtalk.conv.stop_reading') : t('performance.dtalk.conv.read_aloud')}
              >
                {speakerOn || isSpeaking ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <VoiceInput
                variant="inline"
                lang={STT_LANG[effIdioma]}
                onRecordingChange={(rec) => {
                  setRecording(rec);
                  if (rec) stopSpeaking(); // que el mic no capture a la propia agente
                }}
                onTranscript={(txt) => {
                  lastInputWasVoiceRef.current = true;
                  focusAfterVoiceRef.current = true;
                  // `maxLength` no alcanza a un setState, y dictar es justo donde
                  // uno se pasa del tope sin darse cuenta.
                  setInput(prev => (prev ? `${prev} ${txt}` : txt).slice(0, DTALK_MAX_MESSAGE_CHARS));
                }}
              />
              {/* hidden (no unmount) para no perder lo tipeado mientras graba */}
              <Textarea ref={textareaRef} value={input}
                onChange={e => { setInput(e.target.value); if (sendError?.motivo === 'too_long') setSendError(null); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder={t('performance.dtalk.conv.input_placeholder')} rows={1} maxLength={DTALK_MAX_MESSAGE_CHARS}
                className={cn('flex-1 resize-none text-sm min-h-0 max-h-40 overflow-y-auto', recording && 'hidden')} disabled={processMessage.isPending} />
              <Button onClick={() => send(input)} disabled={!input.trim() || processMessage.isPending} size="icon" className={cn('rounded-full h-9 w-9 shrink-0 bg-teal-600 hover:bg-teal-700', recording && 'hidden')}>
                {processMessage.isPending ? <WinclapLoader size={14} /> : <Send className="h-4 w-4" />}
              </Button>
             </div>
             {/* Solo cerca del tope: una respuesta larga tiene que avisar ANTES de enviarse */}
             {!recording && input.length > DTALK_MAX_MESSAGE_CHARS * 0.9 && (
               <p className={cn('mt-1 text-right text-[10px] tabular-nums', input.length >= DTALK_MAX_MESSAGE_CHARS ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                 {t('performance.dtalk.conv.chars_left', { restantes: DTALK_MAX_MESSAGE_CHARS - input.length, max: DTALK_MAX_MESSAGE_CHARS })}
               </p>
             )}
            </div>
          )}
        </Card>

        {/* Progreso + comprensión */}
        <Card className="lg:sticky lg:top-4">
          <CardContent className="pt-5 space-y-4">
            {/* Sin contador "X de N preguntas" (feedback Sol 06/08): solo la barra */}
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">{t('performance.dtalk.conv.progress_heading')}</p>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${displayProgress}%` }} />
            </div>
            <p className="text-sm font-semibold pt-2">{t('performance.dtalk.conv.understanding')}</p>
            <div className="space-y-2.5">
              {understanding.map(u => (
                <div key={u.key} className="flex items-center gap-2 text-sm">
                  <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', u.done ? 'bg-teal-500' : 'bg-muted-foreground/25')} />
                  <span className={u.done ? 'text-foreground' : 'text-muted-foreground'}>{t(`performance.dtalk.conv.${u.key}`)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}

function SummaryPanel({ record }: { record: any }) {
  const { t } = useTranslation();
  if (!record?.conclusionParaValidar && !record?.conclusionValidada && !record?.estadoActual) {
    return <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('performance.dtalk.conv.summary_empty')}</CardContent></Card>;
  }
  const rows: Array<[string, any]> = [
    ['detail.estado_actual', record.estadoActual],
    ['detail.aspiracion_principal', record.aspiracionPrincipal],
    ['detail.aspiraciones_secundarias', (record.aspiracionesSecundarias ?? []).join(', ')],
    ['detail.motivaciones', (record.motivaciones ?? []).join(', ')],
    ['detail.competencias', (record.competenciasADesarrollar ?? []).join(', ')],
    ['detail.responsabilidades', (record.responsabilidadesInteres ?? []).join(', ')],
  ];
  const conclusion = record.conclusionValidada ?? record.conclusionParaValidar;
  return (
    <Card><CardContent className="pt-5 space-y-4">
      <p className="text-sm font-semibold">{t('performance.dtalk.conv.conclusion_heading')}</p>
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k}><p className="text-xs font-semibold text-teal-600">{t(`performance.dtalk.${k}`)}</p><p className="text-sm text-foreground/90">{v}</p></div>
      ))}
      {conclusion && <div className="rounded-lg bg-muted/50 p-3 text-sm italic">"{conclusion}"</div>}
    </CardContent></Card>
  );
}

function safeTime(ts: string): string {
  try { return format(new Date(ts), 'HH:mm'); } catch { return ''; }
}

function fmtDate(d?: string): string {
  if (!d) return '';
  try { return format(new Date(`${d}T00:00:00`), 'dd/MM/yyyy'); } catch { return d; }
}
